import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TextField,
  IconButton,
  Avatar,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WalletIcon from '@mui/icons-material/Wallet';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import './webstoreAdminUsers.scss';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const WebstoreAdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditInput, setCreditInput] = useState('');
  const [openModal, setOpenModal] = useState('');

  const itemsPerPage = 8;
  const isMobile = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/webstore-users`, {
        headers: { 'x-admin-auth': 'navarrojuben' }
      });
      setUsers(res.data);
    } catch {
      toast.error('Failed to fetch users');
    }
  };

  const fetchOrderCount = async (userId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/webstore-orders/user/${userId}/count`, {
        headers: { 'x-admin-auth': 'navarrojuben' }
      });
      return res.data.count || 0;
    } catch {
      return 0;
    }
  };

  const fetchMessageCount = async (userId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/webstore-chat/user/${userId}/count`, {
        headers: { 'x-admin-auth': 'navarrojuben' }
      });
      return res.data.count || 0;
    } catch {
      return 0;
    }
  };

  const handleSearch = (e) => {
    setUserSearch(e.target.value);
    setCurrentPage(1);
  };

  const filteredUsers = users.filter((u) =>
    u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const openModalHandler = async (type, user) => {
    let userWithExtras = { ...user };

    if (type === 'view' || type === 'delete') {
      const [orderCount, messageCount] = await Promise.all([
        fetchOrderCount(user._id),
        fetchMessageCount(user._id)
      ]);
      userWithExtras.orderCount = orderCount;
      userWithExtras.messages = messageCount;
    }

    if (type === 'credit') {
      setCreditInput(user.njCredits);
    }

    setSelectedUser(userWithExtras);
    setOpenModal(type);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setCreditInput('');
    setOpenModal('');
  };

  const confirmCreditChange = async () => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/webstore-users/${selectedUser._id}/credits`,
        { njCredits: creditInput },
        { headers: { 'x-admin-auth': 'navarrojuben' } }
      );
      toast.success('Credits updated');
      fetchUsers();
    } catch {
      toast.error('Failed to update credits');
    } finally {
      closeModal();
    }
  };

  const confirmUserDelete = async () => {
    try {
      await Promise.all([
        axios.delete(`${API_BASE_URL}/api/webstore-users/${selectedUser._id}`, {
          headers: { 'x-admin-auth': 'navarrojuben' }
        }),
        axios.delete(`${API_BASE_URL}/api/webstore-orders/by-user/${selectedUser._id}`, {
          headers: { 'x-admin-auth': 'navarrojuben' }
        }),
        axios.delete(`${API_BASE_URL}/api/webstore-chat/by-user/${selectedUser._id}`, {
          headers: { 'x-admin-auth': 'navarrojuben' }
        })
      ]);

      toast.success('User, orders, and messages deleted.');
      fetchUsers();
    } catch {
      toast.error('Failed to delete user data');
    } finally {
      closeModal();
    }
  };

  return (
    <div className="admin-users-wrapper">
      <h2>Registered Users</h2>
      <div className="search-bar">
        <TextField
          fullWidth
          placeholder="Search by username or email"
          value={userSearch}
          onChange={handleSearch}
          size="small"
        />
      </div>

      <TableContainer component={Paper} className="user-table-wrapper" sx={{ overflowX: 'auto' }}>
        <Table className="user-table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Avatar</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Credits</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user._id}>
                <TableCell>
                  {user.avatar ? (
                    <Avatar src={user.avatar} alt="avatar" />
                  ) : (
                    <AccountCircleIcon />
                  )}
                </TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.njCredits}</TableCell>
                <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => openModalHandler('credit', user)}><WalletIcon /></IconButton>
                  <IconButton onClick={() => openModalHandler('view', user)}><VisibilityIcon /></IconButton>
                  <IconButton onClick={() => openModalHandler('delete', user)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal remains unchanged */}
      {openModal && selectedUser && (
        <div className="custom-modal-overlay" onClick={closeModal}>
          <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>

            {openModal === 'credit' && (
              <>
                <h3>Edit Credits</h3>
                <TextField
                  type="number"
                  fullWidth
                  value={creditInput}
                  onChange={(e) => setCreditInput(e.target.value)}
                />
                <div className="modal-actions">
                  <button onClick={confirmCreditChange}>Confirm</button>
                  <button onClick={closeModal}>Cancel</button>
                </div>
              </>
            )}

            {openModal === 'delete' && (
              <>
                <h3>Delete User <span style={{ color: '#ff4d4f' }}>{selectedUser.username}</span>?</h3>
                <p>This will permanently delete:</p>
                <ul>
                  <li><strong>{selectedUser.orderCount}</strong> order(s)</li>
                  <li><strong>{selectedUser.messages}</strong> message(s)</li>
                  <li><strong>{selectedUser.njCredits}</strong> credit(s)</li>
                </ul>
                <p style={{ color: '#ff4d4f' }}>This action cannot be undone.</p>
                <div className="modal-actions">
                  <button className="danger" onClick={confirmUserDelete}>
                    Confirm Delete
                  </button>
                  <button onClick={closeModal}>Cancel</button>
                </div>
              </>
            )}

            {openModal === 'view' && (
              <div className="user-info">
                <div className="avatar-row">
                  {selectedUser.avatar ? (
                    <Avatar src={selectedUser.avatar} alt="avatar" />
                  ) : (
                    <AccountCircleIcon sx={{ fontSize: 60 }} />
                  )}
                </div>
                <p><strong>ID:</strong> {selectedUser._id}</p>
                <p><strong>Username:</strong> {selectedUser.username}</p>
                <p><strong>Name:</strong> {selectedUser.name || '—'}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>Contact Number:</strong> {selectedUser.contactNumber || '—'}</p>
                <p><strong>Address:</strong> {selectedUser.address || '—'}</p>
                <p><strong>Role:</strong> {selectedUser.role}</p>
                <p><strong>Credits:</strong> {selectedUser.njCredits}</p>
                <p><strong>Banned:</strong> {selectedUser.isBanned ? 'Yes' : 'No'}</p>
                <p><strong>Messages:</strong> {selectedUser.messages ?? '—'}</p>
                <p><strong>Orders:</strong> {selectedUser.orderCount ?? '—'}</p>
                <p><strong>Created:</strong> {new Date(selectedUser.createdAt).toLocaleString()}</p>
                <p><strong>Updated:</strong> {new Date(selectedUser.updatedAt).toLocaleString()}</p>
                <div className="modal-actions">
                  <button onClick={closeModal}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebstoreAdminUsers;
